import { isEqual } from 'lodash-unified';
import { httpClientRepository } from '@bg-shared/infrastructure/HttpClient';
import { webSocketsRepository } from '@bg-shared/infrastructure/WebSockets';
import { API_URLS } from '@bg-shared/constants/apiUrls';
import { analyticsApi } from '@bg-shared/business/Analytics';
import { debounceAsync } from '@bg-shared/utils/debounceAsync';
import { BetType } from '@bg-shared/enums/BetType';
import { GameId } from '@bg-shared/enums/GameId';
import { STAGED_BETS_RESPONSE } from '@bg-shared/constants/socketChannels';
import { parseErrorText } from '@bg-shared/utils/parseErrorText';
import { gamesRunsEntity } from '@bg-shared/business/GamesRuns';
import { gamesOptionsEntity } from '@bg-shared/business/GamesOptions';
import { ChipActionType } from '@bg-shared/business/CasinoBetting/enums';
import { gamesMessagesEntity } from '@bg-shared/business/GamesMessages';
import { playerActivityService } from '@bg-shared/business/PlayerActivity';
import { stagedBetsApi, stagedBetsStore } from '@bg-shared/business/StagedBets';
import {
    NOTIFICATION_ERROR_SHOW_TIME,
    NOTIFICATION_SHORT_TIMEOUT,
} from '@bg-shared/constants/constants';
import { GameMessageType } from '@bg-shared/business/GamesMessages/enums';
import { t } from '@bg-shared/business/Translate';
import { bettingLimitsApi } from '@bg-shared/business/BettingLimits/BettingLimits.api';
import { gamesStatusEntity } from '@bg-shared/business/GamesStatus';
import { webAPIIntegration } from '@bg-shared/integrations/WebIntegration/webAPIIntegration';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { totalBetAmountEntity } from '@bg-shared/business/TotalBetAmount';
import { sumAmounts } from '@bg-shared/utils';
import { balanceStore } from '../../Balance';
import { optionIDConverter } from '../utils/optionIDConverter';
import {
    IChipAmounts,
    ILatestPayinResponse,
    IStagedBetEvent,
    IStagedBetsPayload,
} from '../interfaces';
import {
    casinoBetActionsEntity,
    casinoBetAmountsEntity,
    casinoLatestPayinEntity,
    casinoLimitsEntity,
    casinoStagedBetsEntity,
    casinoAutoPlayEntity,
    casinoActiveOptionsClassesEntity,
} from '../store';

export class CasinoBettingApi {
    private readonly STAGED_BETS_DEBOUNCE_TIME = 500;

    protected readonly betActionsStore: ReturnType<typeof casinoBetActionsEntity.store>;

    private readonly betAmountsStore: ReturnType<typeof casinoBetAmountsEntity.store>;

    private readonly latestPayinStore: ReturnType<typeof casinoLatestPayinEntity.store>;

    private readonly casinoAutoPlayStore: ReturnType<typeof casinoAutoPlayEntity.store>;

    private readonly gameRunStore: ReturnType<typeof gamesRunsEntity.store>;

    private readonly gameOptionsStore: ReturnType<typeof gamesOptionsEntity.store>;

    private readonly limitsStore: ReturnType<typeof casinoLimitsEntity.store>;

    private readonly gamesStatusStore: ReturnType<typeof gamesStatusEntity.store>;

    private readonly totalBetAmountStore: ReturnType<typeof totalBetAmountEntity.store>;

    private readonly casinoStagedBetsStore: ReturnType<typeof casinoStagedBetsEntity.store>;

    private readonly activeOptionsClassesStore: ReturnType<
        typeof casinoActiveOptionsClassesEntity.store
    >;

    private lastStagedBetsPayload: IStagedBetsPayload = null;

    private lastBetFailed = false;

    constructor(
        private readonly gameId: GameId,
        private readonly withSwitchView: boolean,
    ) {
        this.betActionsStore = casinoBetActionsEntity.store(gameId);
        this.latestPayinStore = casinoLatestPayinEntity.store(gameId);
        this.casinoAutoPlayStore = casinoAutoPlayEntity.store(gameId);
        this.betAmountsStore = casinoBetAmountsEntity.store(gameId);
        this.gameRunStore = gamesRunsEntity.store(gameId);
        this.gameOptionsStore = gamesOptionsEntity.store(gameId);
        this.limitsStore = casinoLimitsEntity.store(gameId);
        this.gamesStatusStore = gamesStatusEntity.store(gameId);
        this.totalBetAmountStore = totalBetAmountEntity.store(gameId);
        this.activeOptionsClassesStore = casinoActiveOptionsClassesEntity.store(gameId);
        this.casinoStagedBetsStore = casinoStagedBetsEntity.store(gameId);
    }

    private shouldUpdateBetAmounts(response: ILatestPayinResponse): boolean {
        const isCurrentRun = response.recentlyPlayedRunId === this.gameRunStore.value.run.id;
        const isOpenForBets = this.gamesStatusStore.value.openForBets;

        // !isOpenForBets - when switching between betslip and casino view
        return isCurrentRun && (this.withSwitchView ? isOpenForBets : !isOpenForBets);
    }

    private buildStagedBetsPayload(): IStagedBetsPayload {
        return {
            gameId: this.gameId,
            runId:
                this.gamesStatusStore.value.started && this.gameRunStore.value.nextRun?.id
                    ? this.gameRunStore.value.nextRun.id
                    : this.gameRunStore.value.run.id,
            ...(this.gameRunStore.value.round
                ? {
                      runRoundId: this.gameRunStore.value.round.id,
                  }
                : {}),
            bets: Object.entries(this.betAmountsStore.value)
                .map(([optionItemID, amount]) => {
                    const { oddId, items } = optionIDConverter.parse(optionItemID);

                    return {
                        oddId,
                        items,
                        amount,
                    };
                })
                .filter(({ oddId, items, amount }) => {
                    const itemsCount = items?.filter((item) => item)?.length || 0;
                    const option = this.gameOptionsStore.value.options[oddId];
                    const isValid = itemsCount === option.itemsCount && !!option.value;

                    return isValid && amount > 0;
                })
                .map(({ oddId, items, amount }) => ({
                    oddId,
                    items,
                    amount,
                    type: BetType.SINGLE,
                    oddValue: this.gameOptionsStore.value.options[oddId].value,
                })),
        };
    }

    public updateLatestPayin<T = Record<string, unknown>>(): Promise<ILatestPayinResponse & T> {
        return httpClientRepository
            .get<ILatestPayinResponse & T>(`${API_URLS.PLAYER_RECENT_ACTION}/${this.gameId}`)
            .then((response) => {
                this.latestPayinStore.update({
                    currentBetting: {
                        bets: [],
                        runId: null,
                    },
                    recentBets: response.recentRunBets.map((bet) => ({
                        id: bet.id,
                        oddId: bet.oddId,
                        oddClass: bet.oddClass,
                        oddValue: Number(bet.oddValue),
                        amount: Number(bet.amount),
                        items: bet.items,
                    })),
                    recentWin: response.recentWin,
                    runId: response.recentlyPlayedRunId,
                });

                const bets = response.recentRunBets.filter((bet) =>
                    this.gameRunStore.value.round
                        ? bet.runRoundId === this.gameRunStore.value.round.id
                        : true,
                );

                if (this.shouldUpdateBetAmounts(response)) {
                    this.betAmountsStore.update(
                        bets.reduce<IChipAmounts>((result, bet) => {
                            result[optionIDConverter.create(bet.oddId, bet.items)] = Number(
                                bet.amount,
                            );
                            return result;
                        }, {}),
                    );
                }

                if (this.gameRunStore.value.run.id === response.recentlyPlayedRunId) {
                    this.totalBetAmountStore.increase(
                        sumAmounts(bets.map((bet) => Number(bet.amount))),
                    );
                }

                return response;
            })
            .catch(() => null);
    }

    public updateStagedBets(): Promise<void> {
        return stagedBetsApi
            .getStagedBets([this.gameId])
            .then((response) => {
                if (!response.data.length) {
                    return;
                }

                const bets = response.data.find((bet) => bet?.gameId === this.gameId);

                if (!bets) {
                    return;
                }

                const stagedBets = bets?.stagedBets?.bets || [];

                if (
                    bets?.gameId === this.gameId &&
                    stagedBets.length &&
                    ((this.gamesStatusStore.value.openForBets &&
                        bets.runId === this.gameRunStore.value.run.id) ||
                        (!this.gamesStatusStore.value.openForBets &&
                            bets.runId === this.gameRunStore.value.nextRun?.id))
                ) {
                    this.betActionsStore.add(
                        stagedBets.map((bet) => ({
                            type: ChipActionType.REPEAT,
                            amounts: [Number(bet.amount)],
                            optionItemIDs: [optionIDConverter.create(bet.oddId, bet.items)],
                            validated: true,
                            pendingUndo: null,
                        })),
                    );
                    this.betAmountsStore.updateFromActions(this.betActionsStore.value);
                    this.totalBetAmountStore.increase(
                        sumAmounts(stagedBets.map((bet) => bet.amount)),
                    );

                    this.casinoStagedBetsStore.update(stagedBets);
                }
            })
            .catch(() => {});
    }

    public updateLimits(): Promise<void> {
        const optionsList = Object.values(this.gameOptionsStore.value.options);
        const options = !this.activeOptionsClassesStore.value.length
            ? optionsList
            : optionsList.filter(
                  (option) =>
                      !!option.value &&
                      this.activeOptionsClassesStore.value.includes(option.optionClass),
              );

        if (!options.length) {
            return Promise.resolve();
        }

        return bettingLimitsApi
            .getLimits({
                type: BetType.SINGLE,
                options: options.map((option) => ({
                    id: option.id,
                    value: option.value,
                    runId: this.gameRunStore.value.run.id,
                    runRoundId: this.gameRunStore.value.round?.id,
                })),
            })
            .then((limits) => {
                this.limitsStore.update(limits);
            });
    }

    private debounceStagedBet = debounceAsync(
        (payload: IStagedBetsPayload) =>
            httpClientRepository
                .post<void>(API_URLS.STAGED_BETS, {
                    data: payload,
                    timeout: partnerSettingsStore.requestTimeout,
                })
                .then(() => {
                    this.lastBetFailed = false;
                    this.betActionsStore.markAsValidated();
                    this.betActionsStore.resolvePendingUndo();
                    this.casinoStagedBetsStore.update(payload.bets);
                })
                .catch((error) => {
                    this.lastBetFailed = true;
                    this.betActionsStore.filterOutInvalid();
                    this.betActionsStore.removePendingUndoKey();

                    this.betAmountsStore.updateFromActions(this.betActionsStore.value);

                    gamesMessagesEntity.showMessage({
                        message: `${t.string('something_wrong')}${
                            error.status ? `${error.status}` : ''
                        }`,
                        timeout: NOTIFICATION_ERROR_SHOW_TIME,
                        type: GameMessageType.DANGER,
                    });

                    webAPIIntegration(partnerSettingsStore.webIntegrationType, (api) => {
                        api.errorMessage(error?.message);
                    });
                }),
        this.STAGED_BETS_DEBOUNCE_TIME,
    );

    public placeStagedBets() {
        const payload = this.buildStagedBetsPayload();
        const isSamePayload = isEqual(payload, this.lastStagedBetsPayload);
        const isFirstEmptyPayload = !this.lastStagedBetsPayload && !payload.bets.length;

        // lastBetFailed is a flag to prevent chips amount changes in case of network issues and failed previous bet
        if ((isFirstEmptyPayload || isSamePayload) && !this.lastBetFailed) {
            return Promise.resolve();
        }

        this.casinoStagedBetsStore.update(payload.bets);
        this.lastStagedBetsPayload = payload;

        playerActivityService.reset();

        return this.debounceStagedBet(payload);
    }

    public stagedBetsResponseEvent(callback?: (isSuccess: boolean) => void): () => void {
        return webSocketsRepository.on<IStagedBetEvent>(STAGED_BETS_RESPONSE, (event) => {
            if (event?.gameId !== this.gameId) {
                return;
            }

            this.lastStagedBetsPayload = null;
            this.betActionsStore.reset();

            if ('message' in event) {
                const message = parseErrorText({ message: event.message });

                this.betAmountsStore.reset();
                gamesMessagesEntity.showMessage({
                    message,
                    timeout: NOTIFICATION_ERROR_SHOW_TIME,
                    type: GameMessageType.INFO,
                });

                webAPIIntegration(partnerSettingsStore.webIntegrationType, (api) => {
                    api.errorMessage(event.message);
                });

                callback?.(false);

                return;
            }

            balanceStore.updateStagedBalance(event.gameId, 0, event.balance);
            stagedBetsStore.reset();
            casinoStagedBetsEntity.store(event.gameId).reset();

            analyticsApi.trackCasinoPlaceBet(
                event.batch.map((bet) => bet.bet.id),
                this.casinoAutoPlayStore.value.isActive,
            );

            gamesMessagesEntity.showMessage({
                message: t.string('bet_success'),
                timeout: NOTIFICATION_SHORT_TIMEOUT,
                type: GameMessageType.SUCCESS,
            });

            callback?.(true);
        });
    }
}
